/**
 * LiveSiteEventLogsCard.tsx
 * ----------------
 * Card displaying logs belonging to a live site event.
 */

import * as React from 'react';

import { CircleCheckIcon, DotIcon, OfflineOneDriveParachuteIcon } from './FluentSvgIcon';
import { LiveSiteEventLogType } from '../../models/status';
import { formatUtcAsLocalInEnglishLocale } from '../../utilities/time';

/**
 * Component properties
 */

interface LiveSiteEventLogEntry {
    author: string;
    text: string;
    timestamp: string;
    type: LiveSiteEventLogType;
}

interface LiveSiteEventLogsCardProps {
    logs: LiveSiteEventLogEntry[];
}

/**
 * Private functions
 */

function iconForLog (type: LiveSiteEventLogType) {
    switch (type) {
        case 'Mitigate':
            return (
                <div className="live-site-event-logs-card__icon live-site-event-logs-card__icon--highlighted">
                    <svg className="live-site-event-logs-card__icon-orb" width="26" height="26" viewBox="0 0 26 26">
                        <title>Mitigated</title>
                        <circle cx="13" cy="13" r="13" />

                        <OfflineOneDriveParachuteIcon
                            className="live-site-event-logs-card__icon-drawing live-site-event-logs-card__icon-drawing--highlighted"
                            x={5.5}
                            y={5.5} />
                    </svg>
                </div>
            );

        case 'Resolve':
            return (
                <div className="live-site-event-logs-card__icon live-site-event-logs-card__icon--highlighted">
                    <svg className="live-site-event-logs-card__icon-orb" width="26" height="26" viewBox="0 0 26 26">
                        <title>Resolved</title>
                        <circle cx="13" cy="13" r="13" />

                        <CircleCheckIcon
                            className="live-site-event-logs-card__icon-drawing live-site-event-logs-card__icon-drawing--highlighted"
                            x={5.5}
                            y={5.5} />
                    </svg>
                </div>
            );

        default:
            return (
                <div className="live-site-event-logs-card__icon">
                    <DotIcon className="live-site-event-logs-card__icon-drawing" title="Update" />
                </div>
            );
    }
}

/**
 * Card displaying logs belonging to a live site event.
 * @param props Component properties
 * @returns Rendered content
 */
export function LiveSiteEventLogsCard (props: LiveSiteEventLogsCardProps) {
    const { logs } = props;

    return (
        <div className="live-site-event-logs-card ms-depth-8">
            {logs.reverse().map((log, i) => {
                const { author, text, timestamp, type } = log;
                const isLastLog = i === logs.length - 1;
                const dataPartClassNames = [
                    'live-site-event-logs-card__data-part',
                    ...isLastLog ? ['live-site-event-logs-card__data-part--last'] : [],
                ].join(' ');

                return (
                    <div className="live-site-event-logs-card__item" key={i}>
                        <div className="live-site-event-logs-card__timeline-part">
                            {iconForLog(type)}

                            {!isLastLog &&
                                <svg className="live-site-event-logs-card__path" preserveAspectRatio="none" viewBox="0 0 26 1">
                                    <line x1={12.5} x2={12.5} y1={0} y2={1} />
                                </svg>}
                        </div>

                        <div className={dataPartClassNames}>
                            <div className="live-site-event-logs-card__item-title">
                                {author} | {formatUtcAsLocalInEnglishLocale(timestamp)}
                            </div>

                            <div className="live-site-event-logs-card__item-description">
                                {text}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
